import { collection, query, where, getDocs, deleteDoc } from 'firebase/firestore';
import { db } from '../../config';
import { handleFirebaseError } from '../../error-handling';
import { getMemberDetails } from './getMemberDetails';
import { addMemberToOrganization } from './addMemberToOrganization';
import { toast } from 'sonner';
import type { User } from '@/types';

export async function getOrganizationManagers(organizationId: string): Promise<User[]> {
  try {
    // Get manager memberships
    const membersQuery = query(
      collection(db, 'organizationMembers'),
      where('organizationId', '==', organizationId),
      where('role', '==', 'manager')
    );
    
    const snapshot = await getDocs(membersQuery);
    
    // Get user details for each manager
    const managers = await Promise.all(
      snapshot.docs.map(async (doc) => {
        const memberData = doc.data();
        return await getMemberDetails(memberData.userId);
      })
    );

    return managers.filter((m): m is User => m !== null);
  } catch (error) {
    handleFirebaseError(error, 'Error fetching organization managers');
    return [];
  }
}

export async function addManagerToOrganization(
  organizationId: string,
  managerId: string,
  addedBy: string
): Promise<boolean> {
  // Import the updated function that includes auto-group membership
  const { addManagerToOrganization: addManagerWithGroups } = await import('./managers/addManager');
  return addManagerWithGroups(organizationId, managerId, addedBy);
}

export async function removeManagerFromOrganization(
  organizationId: string,
  managerId: string
): Promise<boolean> {
  try {
    const membershipQuery = query(
      collection(db, 'organizationMembers'),
      where('organizationId', '==', organizationId),
      where('userId', '==', managerId),
      where('role', '==', 'manager')
    );
    
    const snapshot = await getDocs(membershipQuery);
    
    if (snapshot.empty) {
      return false;
    }

    await Promise.all(
      snapshot.docs.map(doc => deleteDoc(doc.ref))
    );

    toast.success('Manager removed successfully');
    return true;
  } catch (error) {
    handleFirebaseError(error, 'Error removing manager');
    return false;
  }
}